/**
 * RecordJumpStrip — the Care record's sticky at-a-glance row (care-record
 * restructure): one calm horizontal line of quiet sage pills, each a
 * POPULATED clinical section as "Label N", sticking just under the app
 * chrome while the clinical page scrolls. Each pill is a jump link that
 * emits the section's `#`-hash; the shipped Wave A / G4 machinery
 * (useHashScroll + usePersistedCardOpen's hash force-open) scrolls the
 * card into view and opens it if collapsed — this component only EMITS
 * hashes, it never scrolls itself.
 *
 * Register law: these are CATEGORY / wayfinding pills (quiet sage), never
 * a signal color — a count is orientation, not state. Empty sections are
 * omitted (their card on the record reads as no entries, LP-29). Scoped
 * to the Care record tab only.
 *
 * Reads the ONE aggregate snapshot; no fetch.
 */

import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useIsMobile } from '@/hooks/useIsMobile';
import type { ClientRecordSnapshot } from '@/lib/clientRecord';

/** One strip entry: the card's DOM id (= the hash target), its label,
 *  and how many ACTIVE records the section holds. */
interface JumpSection {
  id: string;
  label: string;
  count: number;
  /** PART 4 (UI polish pass): a Tailwind `bg-*` class in the section's
   *  domain hue — the SAME family as its card's accent (CARD_ACCENTS in
   *  cardSpine.tsx), rendered at full strength (the deep/heading tone,
   *  not the pale band tone) so the ~6px dot actually reads against the
   *  quiet sage pill. The strip doubles as a color legend. */
  dotClass: string;
}

/** The section's ACTIVE rows — the pill count matches what the CM sees
 *  on the card (archived existence rides the page's one "N archived
 *  items · Show" line, LP-40; a pill count above the visible rows reads
 *  as a bug). */
function activeCount(rows: ReadonlyArray<{ isActive: boolean }>): number {
  return rows.filter((r) => r.isActive).length;
}

/**
 * The Care record's sections in page reading order (allergies pin, the
 * appointments band, then the two columns left-to-right). Counts are the
 * section's ACTIVE records; a section with none (even when archived rows
 * exist) is omitted; the page's archived line still counts those rows.
 */
export function jumpSections(record: ClientRecordSnapshot): JumpSection[] {
  return [
    // Allergies: the same softened terracotta as its card heading
    // (cardVariants.tsx AllergiesCard) — not the strong pepper-deep of
    // the Risk-flags dot below.
    {
      id: 'allergies',
      label: 'Allergies',
      count: activeCount(record.allergies),
      dotClass: 'bg-pepper',
    },
    {
      id: 'appointments',
      label: 'Appointments',
      count: activeCount(record.appointments),
      dotClass: 'bg-mist-deep',
    },
    {
      id: 'medications',
      label: 'Medications',
      count: activeCount(record.medications),
      dotClass: 'bg-warm-700',
    },
    {
      id: 'functional-status',
      label: 'Functional',
      count: activeCount(record.functionalStatus),
      dotClass: 'bg-heather-deep',
    },
    {
      id: 'risk-flags',
      label: 'Risk flags',
      count: activeCount(record.riskFlags),
      dotClass: 'bg-pepper-deep',
    },
    {
      id: 'conditions',
      label: 'Conditions',
      count: activeCount(record.conditions),
      dotClass: 'bg-heather-deep',
    },
    {
      id: 'hospital-events',
      label: 'Hospital & rehab',
      count: activeCount(record.hospitalEvents),
      dotClass: 'bg-heather-deep',
    },
  ].filter((s) => s.count > 0);
}

export function RecordJumpStrip({ record }: { record: ClientRecordSnapshot }): ReactNode {
  const location = useLocation();
  // The AppShell header flips 60px ↔ 68px via useIsMobile (760px) — the
  // SAME hook drives this offset. A Tailwind md: class (768px) would open
  // a 761-767px window where the strip's top tucks under the header.
  const isMobile = useIsMobile();
  const sections = jumpSections(record);
  if (sections.length === 0) return null;
  return (
    // Sticks under the AppShell header (h-[60px] mobile / h-[68px]
    // desktop, z-50); the cream ground matches the page so cards slide
    // beneath it cleanly. z stays below the header and dialogs.
    <div
      data-testid="record-jump-strip"
      className={`sticky ${isMobile ? 'top-[60px]' : 'top-[68px]'} z-30 -mt-1 bg-cream py-1.5`}
    >
      <nav
        aria-label="Jump to a section"
        className="flex items-center gap-1.5 overflow-x-auto"
      >
        {sections.map((s) => (
          <Link
            key={s.id}
            // Preserve ?tab=care — a bare hash `to` would drop the search
            // params and bounce the CM off the tab.
            to={{ search: location.search, hash: `#${s.id}` }}
            data-testid={`jump-pill-${s.id}`}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-tight bg-sage-50 px-2.5 py-1 text-[12.5px] font-medium text-sage-700 hover:bg-sage-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          >
            {/* PART 4: the leading dot in the section's domain hue — the
                strip doubles as a color legend. */}
            <span
              aria-hidden="true"
              data-testid={`jump-pill-dot-${s.id}`}
              className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${s.dotClass}`}
            />
            <span className="inline-flex items-baseline gap-1.5">
              {s.label}{' '}
              <span className="font-semibold text-sage-400">{s.count}</span>
            </span>
          </Link>
        ))}
      </nav>
    </div>
  );
}
